import { Link, redirect, useLoaderData, useParams, useSearchParams } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import type { Entry, EntryPage } from "@repo/shared-types";
import { ApiError } from "@repo/api-client";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/session";
import { requireSession } from "@/lib/guards";
import { grantLabel, holds } from "@/lib/grants";
import { partLabel } from "@/lib/event-form";
import { loadSpaceContext, useSpaceContext, type SpaceContext } from "@/lib/events";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { PERSONAL_PATH, tenantPath } from "@/lib/tenants";
import { dayParts, formatMonth, viewerZone } from "@/lib/times";
import { Feel, SharedBadge, partResult } from "@/components/result-fields";
import { SpacePicture } from "@/components/avatar";
import { EventCard } from "@/components/event-card";
import { SpaceBadges } from "@/components/space-badges";
import { SpaceMenu } from "@/components/space-menu";
import { SpacePreviewPage } from "@/components/space-preview";
import { toast } from "@/lib/toast";
import { PageTitle } from "@/components/brand";
import { BackLink, TopBar } from "@/components/navigation";
import { CreateButton } from "@/components/create-button";
import { Button } from "@/components/ui/button";
import { EmptyNote } from "@/components/empty-note";
import { CalendarDays, NotebookText } from "lucide-react";
import { Item, ItemContent, ItemMedia, ItemTitle } from "@/components/ui/item";

/**
 * One space: its feed of events, newest first.
 *
 * Members see what is published. The owner and admins holding manage_events
 * see drafts and scheduled events too, marked as such — because the api sends
 * them, not because this page decides to. Running the space — its members,
 * requests, invitations and settings, and leaving it — is behind the cog,
 * one page each (space-section.tsx).
 *
 * A personal space has no feed: it holds its owner's own results, not events,
 * so /t/me is the log — every result they have recorded, newest by when it
 * was done.
 */

/** Which page the loader warmed: the space's own, or its presentation. */
type LoaderData = { preview: boolean };

export async function spaceLoader({ request, params }: LoaderFunctionArgs) {
  const slug = params.slug ?? "";

  // A personal space has one address people see, /t/me. Reached by its
  // real slug — its id — it goes there instead, before asking the api anything.
  const session = await requireSession(request);
  const personal = session.memberships.find((m) => m.tenant.isPersonal)?.tenant;
  if (personal && slug === personal.slug) throw redirect(PERSONAL_PATH);

  // "me" is passed through as it is: the api resolves it to the caller's own.
  // Its page — the log, or the feed — is read beside the space, not after it.
  const cursor = cursorOf(request.url);
  const listed = slug === "me" ? read(queries.entries(cursor)) : read(queries.events(slug, cursor));
  // A non-member's 404 is the space's own to report, just below.
  listed.catch(() => {});
  let context: SpaceContext;
  try {
    context = await loadSpaceContext(request, slug);
  } catch (error) {
    // Not a member — the space's own routes answer 404, as they answer for
    // a space that does not exist. A public one has a presentation page for
    // them; anything else, or a preview that fails, stays not found.
    if (error instanceof ApiError && error.status === 404) {
      const preview = await read(queries.preview(slug)).catch(() => null);
      if (preview) {
        await read(queries.profile());
        return { preview: true } satisfies LoaderData;
      }
    }
    throw error;
  }
  await (context.tenant.isPersonal ? read(queries.entries(cursor)) : listed);
  return { preview: false } satisfies LoaderData;
}

function cursorOf(url: string): string | null {
  return new URL(url).searchParams.get("cursor");
}

/** Whether they let people in, and so see how many are asking: the cog's count. */
function canManageMembers({ tenant, role, grants }: SpaceContext): boolean {
  return !tenant.isPersonal && holds(role, grants, "manage_members");
}

/**
 * Asking to join from a public space's presentation page, or taking it back.
 * Each says so in a toast, in the words the spaces page uses for the same.
 */
export async function spaceAction({ request, params }: ActionFunctionArgs) {
  const form = await request.formData();
  const slug = params.slug ?? "";
  try {
    switch (String(form.get("intent") ?? "")) {
      case "join":
        await api.tenants.requestToJoin(slug);
        toast("Asked to join. Somebody there decides next.");
        return {};
      case "withdraw":
        await api.tenants.withdrawRequest(slug);
        toast("Request withdrawn.");
        return {};
      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

export default function Space() {
  const { preview } = useLoaderData() as LoaderData;
  const slug = useParams().slug ?? "";
  return preview ? <Presentation slug={slug} /> : <SpaceFeed slug={slug} />;
}

/** A public space somebody is not in: its presentation page instead. */
function Presentation({ slug }: { slug: string }) {
  const [{ data: preview }, { data: profile }] = useSuspenseQueries({
    queries: [queries.preview(slug), queries.profile()],
  });
  return <SpacePreviewPage preview={preview} zone={viewerZone(profile.timeZone).zone} />;
}

function SpaceFeed({ slug }: { slug: string }) {
  const context = useSpaceContext(slug);
  const cursor = useSearchParams()[0].get("cursor");
  // The personal space is the log, and reads as one: its own title, no badges.
  return context.tenant.isPersonal ? (
    <Log context={context} cursor={cursor} />
  ) : (
    <Feed context={context} slug={slug} cursor={cursor} />
  );
}

function Log({ context, cursor }: { context: SpaceContext; cursor: string | null }) {
  const { data: log } = useSuspenseQuery(queries.entries(cursor));
  const base = tenantPath(context.tenant);
  const { zone } = context;

  return (
    <section className="space-y-6">
      <PageTitle of={["Log"]} />
      <TopBar>
        <h1 className="page-title">Log</h1>
      </TopBar>
      <YourLog log={log} cursor={cursor} base={base} zone={zone} />
      <CreateButton label="Log a workout" to="/t/me/entries/new" />
    </section>
  );
}

function Feed({ context, slug, cursor }: { context: SpaceContext; slug: string; cursor: string | null }) {
  const { tenant, role, grants, memberCount, canManageEvents, zone } = context;
  const { data: page } = useSuspenseQuery(queries.events(slug, cursor));
  const managesMembers = canManageMembers(context);
  // On the space's own read: null for anybody who does not answer requests.
  const requestCount = context.requestCount ?? 0;
  const base = tenantPath(tenant);

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        {/* The way back and the cog share the top, as on an event's page, so
            the name below takes the screen's width. */}
        <TopBar>
          <BackLink to="/spaces">Spaces</BackLink>
          <SpaceMenu
            tenant={tenant}
            role={role}
            memberCount={memberCount}
            requestCount={requestCount}
            canManageMembers={managesMembers}
          />
        </TopBar>
        <div className="flex min-w-0 items-start gap-4">
          <SpacePicture space={tenant} owner={context.session.user} size="xl" />
          <div className="min-w-0 space-y-2">
            <PageTitle of={[tenant.name]} />
            <h1 className="page-title wrap-anywhere">{tenant.name}</h1>
            <SpaceBadges tenant={tenant} role={role} />
            {role === "admin" && (
              <p className="text-sm text-muted-foreground">
                {grants.length > 0
                  ? `The owner has given you: ${grants.map(grantLabel).join(", ")}.`
                  : "The owner has not given you any admin powers here yet."}
              </p>
            )}
          </div>
        </div>
      </div>

      {page.events.length === 0 ? (
        <EmptyNote icon={CalendarDays}>
          {cursor ? "No older events." : canManageEvents ? "Nothing posted yet. Post the first event." : "Nothing posted yet."}
        </EmptyNote>
      ) : (
        <ul className="space-y-3">
          {page.events.map((event) => (
            <li key={event.id}>
              <EventCard
                event={event}
                base={base}
                zone={zone}
                space={{ slug: tenant.slug, name: tenant.name, image: tenant.image }}
                canManage={canManageEvents}
              />
            </li>
          ))}
        </ul>
      )}

      <Pages base={base} cursor={cursor} next={page.nextCursor} older="Older events →" />
      {canManageEvents && <CreateButton label="New event" to={`${base}/events/new`} />}
    </section>
  );
}

/**
 * The log: every result its owner has recorded, private and shared, newest by
 * when each was done. A copy of each event as it was, so it outlives the
 * event, the space and the owner's membership of it.
 */
function YourLog({
  log,
  cursor,
  base,
  zone,
}: {
  log: EntryPage;
  cursor: string | null;
  base: string;
  zone: string;
}) {
  const months = byMonth(log.entries, zone);
  return (
    <section className="space-y-6" aria-label="Your log">
      {log.entries.length === 0 ? (
        <EmptyNote icon={NotebookText}>
          {cursor
            ? "Nothing older."
            : "Nothing logged yet. Record a result from an event in a space you belong to, or log a workout of your own."}
        </EmptyNote>
      ) : (
        months.map(({ month, entries }) => (
          <section key={month} className="space-y-2" aria-label={month}>
            <h2 className="section-label">{month}</h2>
            <ul className="space-y-2">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <LogCard entry={entry} zone={zone} />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      <Pages base={base} cursor={cursor} next={log.nextCursor} older="Older →" />
    </section>
  );
}

/** Newer and older pages, of the feed and of the log: buttons, as links. */
function Pages({
  base,
  cursor,
  next,
  older,
}: {
  base: string;
  cursor: string | null;
  next: string | null;
  older: string;
}) {
  if (!cursor && !next) return null;
  return (
    <nav className="flex justify-between">
      {cursor ? (
        <Button asChild variant="outline" size="lg">
          <Link to={base}>← Newest</Link>
        </Button>
      ) : (
        <span />
      )}
      {next && (
        <Button asChild variant="outline" size="lg">
          <Link to={`${base}?cursor=${encodeURIComponent(next)}`}>{older}</Link>
        </Button>
      )}
    </nav>
  );
}

/** The log's entries in the months they were done, as the viewer reads them, newest first as they came. */
function byMonth(entries: Entry[], zone: string): { month: string; entries: Entry[] }[] {
  const months: { month: string; entries: Entry[] }[] = [];
  for (const entry of entries) {
    const month = formatMonth(entry.performedAt, zone);
    const last = months.at(-1);
    if (last?.month === month) last.entries.push(entry);
    else months.push({ month, entries: [entry] });
  }
  return months;
}

function LogCard({ entry, zone }: { entry: Entry; zone: string }) {
  const { weekday, day } = dayParts(entry.performedAt, zone);
  return (
    <Item asChild variant="outline" className="flex-nowrap items-start bg-card">
      <Link to={`/t/me/entries/${entry.id}`}>
        {/* The day it was done, as a calendar's leaf. */}
        <ItemMedia className="size-12 flex-col gap-0 rounded-xl bg-muted text-center">
          <span className="text-xs font-medium text-muted-foreground">{weekday}</span>
          <span className="text-lg leading-none font-semibold">{day}</span>
        </ItemMedia>
        <ItemContent className="min-w-0">
          <ItemTitle className="line-clamp-none w-auto text-base leading-tight font-semibold wrap-anywhere">
            {entry.title}
          </ItemTitle>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {entry.spaceName ?? `On your own · ${entry.sportLabel}`}
            </span>
            <SharedBadge shared={entry.shared} />
          </div>
          <ul className="space-y-0.5 pt-1 text-sm text-foreground">
            {entry.parts.map((part, index) => (
              <li key={part.id} className={part.recorded ? "" : "text-muted-foreground"}>
                {partLabel(index)}. {part.title} — {partResult(part, !entry.level)} <Feel feel={part.feel} />
              </li>
            ))}
          </ul>
        </ItemContent>
        {entry.photo?.status === "ready" && entry.photo.url && (
          <ItemMedia variant="image" className="size-16">
            <img src={entry.photo.url} alt="" loading="lazy" className="bg-muted" />
          </ItemMedia>
        )}
      </Link>
    </Item>
  );
}
